/**
 * bezierPipeline — flowing bezier ribbon.
 *
 * A single ribbon in 3D space, defined by a parametric bezier curve.
 * The ribbon is rendered as a series of segments. As the camera
 * orbits, the ribbon twists and flows through space.
 *
 * The ribbon has a gradient color from start to end. Audio drives
 * the speed of motion and the color saturation.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BezierPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float distToSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}

// Position on a 3D bezier curve at parameter t (0..1).
vec3 bezier3(float t) {
  // Control points in 3D — form an interesting looping path.
  float angle = t * 6.2831853 + uTime * (0.3 + bass * 0.5);
  float radius = 0.5 + 0.2 * sin(t * 6.2831853 * 3.0);
  float yPos = (t - 0.5) * 1.5 + 0.3 * sin(t * 12.0);
  // Add some 3D complexity.
  float wobble = sin(t * 6.2831853 * 5.0 + uTime) * 0.15;
  vec3 p = vec3(
    radius * cos(angle) + wobble * cos(t * 6.2831853 * 2.0),
    yPos,
    radius * sin(angle) + wobble * sin(t * 6.2831853 * 2.0)
  );
  return p;
}

vec2 project3D(vec3 p) {
  // Slow rotation.
  float angle = uTime * 0.2;
  float ca = cos(angle), sa = sin(angle);
  vec3 rotated = vec3(ca * p.x - sa * p.z, p.y, sa * p.x + ca * p.z);
  // Orthographic projection.
  return rotated.xy * 1.4;
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background: deep gradient.
  vec3 col = vec3(0.04, 0.05, 0.10) + vec3(0.10, 0.05, 0.18) * exp(-length(p) * 1.0);

  // Sample the curve at many points to find the minimum distance.
  const int N_SEG = 96;
  float minDist = 100.0;
  float minT = 0.0;
  vec3 minColor = vec3(0.0);
  for (int i = 0; i < N_SEG; i++) {
    if (i >= N_SEG - 1) break;
    float t1 = float(i) / float(N_SEG);
    float t2 = float(i + 1) / float(N_SEG);
    vec2 pa = project3D(bezier3(t1));
    vec2 pb = project3D(bezier3(t2));
    float d = distToSeg(p, pa, pb);
    if (d < minDist) {
      minDist = d;
      minT = (t1 + t2) * 0.5;
      // Color: gradient along the curve (deep blue → cyan → magenta → gold).
      float h = minT;
      minColor = vec3(
        0.5 + 0.5 * sin(h * 6.2831853 * 2.0 + 0.0),
        0.5 + 0.5 * sin(h * 6.2831853 * 2.0 + 2.094),
        0.5 + 0.5 * sin(h * 6.2831853 * 2.0 + 4.189)
      );
    }
  }

  // Glow around the ribbon.
  col += minColor * exp(-minDist * minDist * 5000.0) * 2.0;
  // Outer halo.
  col += minColor * exp(-minDist * minDist * 200.0) * 0.15;
  // Audio: brightness boost with high.
  col *= 1.0 + high * 0.3;

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeBezierBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeBezierRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBezierPipeline(gl: WebGL2RenderingContext): BezierPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawBezier(
  gl: WebGL2RenderingContext,
  pipe: BezierPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
